import _keyBy from 'lodash/keyBy'
import _includes from 'lodash/includes'
import _values from 'lodash/values'

import IsAnyOfInputs from './IsAnyOfInputs'
import NumericInputs from './NumericInputs'
import TimeInputs from './TimeInputs'
import ClearbitNumericInputs from './ClearbitNumericInputs'
import DemandbaseWatchlistInputs from './DemandbaseWatchlistInputs'
import OnlineInputs from './OnlineInputs'
import StringInputs from './StringInputs'
import TeamSelectInputs from './TeamSelectInputs'
import RegexInputs, { OPERATORS as REGEX_OPERATORS } from './RegexInputs'
import ScrollPercentageInput from './ScrollPercentageInput'
import ExitIntentInput from './ExitIntentInput'
import OutboundSessionInput from './OutboundSessionInput'

import { IsoToCountriesNames } from 'constants/ISOCountries'
import { UspsCodesToStateName } from 'constants/UspsStates'
import { DemandbaseAudiences } from 'constants/DemandbaseAudiences'
import DemandbaseEmployeeRanges from 'constants/DemandbaseEmployeeRanges'
import DemandbaseRevenueRanges from 'constants/DemandbaseRevenueRanges'
import CLEARBIT_TAGS from 'constants/ClearbitTags'
import CLEARBIT_TECH from 'constants/ClearbitTech'
import CLEARBIT_REVENUE_RANGES from 'constants/ClearbitRevenueRanges'
import { StringsInput } from 'components/playbook/components/TargetingDisplay/Inputs'

export const DeviceTypes = [
  { value: 'mobile', label: 'mobile' },
  { value: 'tablet', label: 'tablet' },
  { value: 'desktop', label: 'desktop' },
]

const Browser = {
  display: 'Device',
  default: { field: 'device', operator: 'isAnyOf', value: [DeviceTypes[0].value]},
  component(props) {
    return <IsAnyOfInputs options={DeviceTypes} {...props} />
  },
}

const OutboundSession = {
  display: 'Outbound Session',
  default: { field: 'outboundSession', operator: 'is', value: true },
  component: OutboundSessionInput,
}

const CountryCodeOptions = _.map(IsoToCountriesNames, (label, value) => {
  return { value, label }
})

const CountryNameOptions = _.map(IsoToCountriesNames, (label, value) => {
  return { label, value: label }
})

const DemandbaseEmployeeRangeOptions = _.map(DemandbaseEmployeeRanges, (value) => {
  return { label: value, value }
})

const DemandbaseRevenueRangeOptions = _.map(DemandbaseRevenueRanges, (value) => {
  return { label: value, value }
})

const CountryCode = {
  display: 'Country',
  default: { field: 'geoCountryCode', operator: 'isAnyOf', value: [CountryCodeOptions[0].value]},
  component(props) {
    return <IsAnyOfInputs options={CountryCodeOptions} {...props} />
  },
}

const UsStateValues = _.map(UspsCodesToStateName, label => {
  return { value: label, label }
})

const UsState = {
  display: 'US States',
  default: { field: 'geoState', operator: 'isAnyOf', value: [UsStateValues[0].value]},
  component(props) {
    return <IsAnyOfInputs options={UsStateValues} {...props} />
  },
}

const IpAddress = {
  display: 'IP Address',
  default: {
    field: 'geoIp',
    operator: 'isAnyOf',
    value: [''],
    placeholder: 'example: 192.0.2.2',
  },
  component(props) {
    return <StringInputs optionTypes={['isAnyOf', 'notIsAnyOf']} {...props} />
  },
}

const SiteVisits = {
  display: '# Site Visits',
  default: { field: 'siteVisits', operator: 'is', value: 1 },
  component: NumericInputs,
}

const DaysSinceLastVisit = {
  display: '# Days Since Last Visit',
  default: { field: 'daysSinceLastVisit', operator: 'is', value: 1 },
  component: NumericInputs,
}

const UrlRegex = {
  display: 'URL (Regex)',
  default: {
    field: 'url',
    operator: 'isMatch',
    value: [''],
    placeholder: 'example: drift\\.com\\/blog$',
  },
  component: RegexInputs,
}

const Url = {
  display: 'URL',
  default: {
    field: 'url',
    operator: 'includesAnyOf',
    value: [''],
    placeholder: 'example: drift.com/blog',
  },
  component: StringInputs,
}

const UrlPath = {
  display: 'URL Path',
  default: {
    field: 'urlPath',
    operator: 'isAnyOf',
    value: [''],
    placeholder: 'example: /index.html',
  },
  component: StringInputs,
}

const UrlHost = {
  display: 'URL Host',
  default: {
    field: 'urlHost',
    operator: 'isAnyOf',
    value: [''],
    placeholder: 'example: app.drift.com',
  },
  component: StringInputs,
}

const UrlQuery = {
  display: 'URL Query Param',
  default: {
    field: 'urlQuery',
    operator: 'isAnyOf',
    value: [''],
    placeholder: 'example: query=value',
  },
  component: StringInputs,
}

const Cookie = {
  display: 'Cookie Name=Value',
  default: {
    field: 'cookie',
    operator: 'isAnyOf',
    value: [''],
    placeholder: 'example: cookieName=value',
  },
  component: StringInputs,
}

const Referrer = {
  display: 'Referrer',
  default: {
    field: 'referrer',
    operator: 'includesAnyOf',
    value: [''],
    placeholder: 'example: http://www.drift.com/',
  },
  component: StringInputs,
}

const ScrollPercentage = {
  display: 'Scroll Percentage',
  default: { field: 'scrollPct', operator: 'greaterThan', value: 75 },
  component: ScrollPercentageInput,
}

const SecondsOnPage = {
  display: 'Time on Page (seconds)',
  default: { field: 'secondsOnPage', operator: 'greaterThan', value: 30 },
  component: TimeInputs,
}

const SecondsOnSite = {
  display: 'Time on Site (seconds)',
  default: { field: 'secondsOnSite', operator: 'greaterThan', value: 30 },
  component: TimeInputs,
}

const Team = {
  display: 'Team',
  default: { field: 'teamAvailability', operator: 'isAvailable', value: [''] },
  component: TeamSelectInputs,
}

const IsOnline = {
  display: 'Drift Widget Status',
  default: { field: 'online', operator: 'is', value: true },
  component: OnlineInputs,
}

const OnExitIntent = {
  display: 'On Exit Intent',
  default: { field: 'exitIntent', operator: 'is', value: true },
  component: ExitIntentInput,
}

const DemandbaseAudience = {
  display: 'Demandbase - Audience',
  default: {
    field: 'demandbase_audience',
    operator: 'isAnyOf',
    value: [DemandbaseAudiences[0].value],
  },
  requiredIntegration: 'demandbase',
  component(props) {
    return <IsAnyOfInputs options={DemandbaseAudiences} {...props} />
  },
}

const DemandbaseWatchlist = {
  display: 'Demandbase - Watch List',
  default: {
    field: 'demandbase_watchlist',
    operator: 'isInWatchlist',
    value: [''],
    placeholder: 'example: accounts_owner:dave'
  },
  requiredIntegration: 'demandbase',
  component: DemandbaseWatchlistInputs,
}

const DemandbaseCompanyName = {
  display: 'Demandbase - Company Name',
  default: {
    field: 'demandbase_company_name',
    operator: 'isAnyOf',
    value: [''],
  },
  requiredIntegration: 'demandbase',
  component: StringInputs,
}

const DemandbaseIndustry = {
  display: 'Demandbase - Industry',
  default: {
    field: 'demandbase_industry',
    operator: 'isAnyOf',
    value: [''],
  },
  requiredIntegration: 'demandbase',
  component(props) {
    const industryOptions = _.map(props.demandbaseIndustries, industry => {
      return { label: industry, value: industry }
    })

    return <IsAnyOfInputs 
      options={industryOptions}
      isCreatable={true}
      placeholder="Type to create custom value" 
      {...props} />
  },
}

const DemandbaseEmployeeRange = {
  display: 'Demandbase - Employee Range',
  default: {
    field: 'demandbase_employee_range',
    operator: 'isAnyOf',
    value: [DemandbaseEmployeeRangeOptions[0].value],
  },
  requiredIntegration: 'demandbase',
  component(props) {
    return <IsAnyOfInputs 
      options={DemandbaseEmployeeRangeOptions}
      isCreatable={true}
      placeholder="Type to create custom value"
     {...props} />
  },
}

const DemandbaseEmployeeCount = {
  display: 'Demandbase - Employee Count',
  default: {
    field: 'demandbase_employee_count',
    operator: 'greaterThan',
    value: 50,
  },
  requiredIntegration: 'demandbase',
  component: NumericInputs,
}

const DemandbaseCity = {
  display: 'Demandbase - City',
  default: {
    field: 'demandbase_city',
    operator: 'isAnyOf',
    value: [''],
  },
  requiredIntegration: 'demandbase',
  component: StringInputs,
}

const DemandbaseState = {
  display: 'Demandbase - State',
  default: {
    field: 'demandbase_state',
    operator: 'isAnyOf',
    value: [UsStateValues[0].value],
  },
  requiredIntegration: 'demandbase',
  component(props) {
    return <IsAnyOfInputs options={UsStateValues} {...props} />
  },
}

const DemandbaseCountryCode = {
  display: 'Demandbase - Country Code',
  default: {
    field: 'demandbase_country_code',
    operator: 'isAnyOf',
    value: [CountryCodeOptions[0].value],
  },
  requiredIntegration: 'demandbase',
  component(props) {
    return <IsAnyOfInputs options={CountryCodeOptions} {...props} />
  },
}

const DemandbaseMarketingAlias = {
  display: 'Demandbase - Marketing Alias',
  default: {
    field: 'demandbase_marketing_alias',
    operator: 'isAnyOf',
    value: [''],
  },
  requiredIntegration: 'demandbase',
  component: StringInputs,
}

const DemandbaseSubIndustry = {
  display: 'Demandbase - Sub Industry',
  default: {
    field: 'demandbase_sub_industry',
    operator: 'isAnyOf',
    value: [''],
  },
  requiredIntegration: 'demandbase',
  component(props) {
    const subIndustryOptions = _.map(props.demandbaseSubIndustries, industry => {
      return { label: industry, value: industry }
    })

    return <IsAnyOfInputs 
      options={subIndustryOptions} 
      isCreatable={true}
      placeholder="Type to create custom value" 
      {...props} />
  },
}

const DemandbaseAnnualSales = {
  display: 'Demandbase - Annual Sales',
  default: { field: 'demandbase_annual_sales', operator: 'greaterThan', value: 400000 },
  requiredIntegration: 'demandbase',
  component: NumericInputs,
}

const DemandbaseRevenueRange = {
  display: 'Demandbase - Revenue Range',
  default: {
    field: 'demandbase_revenue_range',
    operator: 'isAnyOf',
    value: ['']
  },
  requiredIntegration: 'demandbase',
  component(props) {
    return <IsAnyOfInputs 
      options={DemandbaseRevenueRangeOptions} 
      {...props} 
      isCreatable={true}
      placeholder="Type to create custom value" />
  },
}

const DemandbaseCountryName = {
  display: 'Demandbase - Country Name',
  default: {
    field: 'demandbase_country_name',
    operator: 'isAnyOf',
    value: [CountryNameOptions[0].value],
  },
  requiredIntegration: 'demandbase',
    component(props) {
    return <IsAnyOfInputs options={CountryNameOptions} {...props} />
  },
}

const ClearbitName = {
  display: 'Drift Intel - Name',
  default: {
    field: 'clearbit_name',
    operator: 'isAnyOf',
    value: [''],
    placeholder: 'example: Drift',
  },
  gates: 'premium_enterprise',
  component: StringInputs,
}

const ClearbitDomain = {
  display: 'Drift Intel - Domain',
  default: {
    field: 'clearbit_domain',
    operator: 'isAnyOf',
    value: [''],
    placeholder: 'example: drift.com',
  },
  gates: 'premium_enterprise',
  component: StringInputs,
}

const ClearbitRevenueOptions = CLEARBIT_REVENUE_RANGES.map(range => ({ value: range, label: range }))
const ClearbitRevenue = {
  display: 'Drift Intel - Revenue',
  default: { field: 'clearbit_revenue', operator: 'isAnyOf', value: [CLEARBIT_REVENUE_RANGES[0]]},
  gates: 'premium_enterprise',
  component(props) {
    return <IsAnyOfInputs options={ClearbitRevenueOptions} {...props} />
  },
}

const ClearbitTagOptions = _.map(CLEARBIT_TAGS, label => {
  return { value: label, label }
})

const ClearbitTechOptions = _.map(CLEARBIT_TECH, ({ value, label }) => {
  return { value, label }
})

const ClearbitTags = {
  display: 'Drift Intel - Tags',
  default: { field: 'clearbit_tags', operator: 'isAnyOf', value: [CLEARBIT_TAGS[0]]},
  gates: 'premium_enterprise',
  component(props) {
    return <IsAnyOfInputs options={ClearbitTagOptions} {...props} />
  },
}

const ClearbitTech = {
  display: 'Drift Intel - Technology',
  default: { field: 'clearbit_tech', operator: 'isAnyOf', value: [CLEARBIT_TECH[0].value]},
  gates: 'premium_enterprise',
  component(props) {
    return <IsAnyOfInputs options={ClearbitTechOptions} {...props} />
  },
}

const ClearbitEmployees = {
  display: 'Drift Intel - # of Employees',
  default: { field: 'clearbit_employees', operator: 'greaterThan', value: 50 },
  gates: 'premium_enterprise',
  component: ClearbitNumericInputs,
}

const ClearbitRaised = {
  display: 'Drift Intel - Amount Raised',
  default: { field: 'clearbit_raised', operator: 'greaterThan', value: 1000000 },
  gates: 'premium_enterprise',
  component: ClearbitNumericInputs,
}

const ClearbitAlexaGlobal = {
  display: 'Drift Intel - Alexa Global Ranking',
  default: { field: 'clearbit_alexa_global', operator: 'lessThan', value: 300000 },
  gates: 'premium_enterprise',
  component: ClearbitNumericInputs,
}

const ClearbitAlexaUS = {
  display: 'Drift Intel - Alexa US Ranking',
  default: { field: 'clearbit_alexa_us', operator: 'lessThan', value: 300000 },
  gates: 'premium_enterprise',
  component: ClearbitNumericInputs,
}

const ClearbitHeadquarterState = {
  display: 'Drift Intel - Headquarter State',
  default: { field: 'clearbit_state', operator: 'isAnyOf', value: [UsStateValues[0].value] },
  gates: 'premium_enterprise',
  component(props) {
    return <IsAnyOfInputs options={UsStateValues} {...props} />
  },
}

const ClearbitHeadquarterCountry = {
  display: 'Drift Intel - Headquarter Country',
  default: { field: 'clearbit_country', operator: 'isAnyOf', value: [CountryCodeOptions[0].value] },
  gates: 'premium_enterprise',
  component(props) {
    return <IsAnyOfInputs options={CountryCodeOptions} {...props} />
  },
}

const ClearbitHeadquarterPostalCode = {
  display: 'Drift Intel - Headquarter Postal Code',
  default: { field: 'clearbit_postal_code', operator: 'isAnyOf', value: [''] },
  gates: 'premium_enterprise',
  component: StringInputs,
}

const TargetConditionTypeMap = {
  Browser,
  OutboundSession,
  SiteVisits,
  DaysSinceLastVisit,
  Url,
  UrlPath,
  UrlHost,
  UrlQuery,
  UrlRegex,
  Referrer,
  Cookie,
  CountryCode,
  UsState,
  IpAddress,
  ScrollPercentage,
  SecondsOnPage,
  SecondsOnSite,
  Team,
  IsOnline,
  ClearbitName,
  ClearbitDomain,
  ClearbitRevenue,
  ClearbitTags,
  ClearbitTech,
  ClearbitEmployees,
  ClearbitRaised,
  ClearbitAlexaGlobal,
  ClearbitAlexaUS,
  ClearbitHeadquarterState,
  ClearbitHeadquarterCountry,
  ClearbitHeadquarterPostalCode,
  DemandbaseAudience,
  DemandbaseWatchlist,
  DemandbaseCompanyName,
  DemandbaseIndustry,
  DemandbaseEmployeeCount,
  DemandbaseCity,
  DemandbaseState,
  DemandbaseCountryCode,
  DemandbaseCountryName,
  DemandbaseAnnualSales,
  DemandbaseMarketingAlias,
  DemandbaseSubIndustry,
  DemandbaseRevenueRange,
  DemandbaseEmployeeRange,
  OnExitIntent,
}

const TargetConditionTypesByFieldName = _keyBy(_values(TargetConditionTypeMap), 'default.field')

export const getTargetCondition = (fieldName, operator) => {
  if (fieldName === 'url') {
    if (_includes(REGEX_OPERATORS, operator)) {
      return UrlRegex
    } else {
      return Url
    }
  }

  return TargetConditionTypesByFieldName[fieldName]
}

export default TargetConditionTypeMap



// WEBPACK FOOTER //
// ./src/scripts/components/CampaignWizards/TargetingInputArea/TargetConditionInput/TargetConditionEditModal/TargetConditionTypes.js